// Configuración · costo logístico (pedido de Rodrigo, 2026-10-14; docs/39). La tarifa de recargo por
// producto, como la de Random: pesos CON IVA por unidad que, en las FACTURAS de una orden de despacho
// cuando quien factura elige el desglose (2026-10-21: por operación, no por cliente), salen del precio del producto y van a una línea «LOGISTICA». El cliente paga lo mismo.
// Se edita de a uno en la grilla o en masa: «x % del precio de la lista», con simulación previa.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { coincide } from '../comun/coincide';

interface Fila {
  productoId: string;
  sku: string;
  nombre: string;
  precioBase: string | null;
  piso: string | null;
  general: string | null;
  cliente: string | null;
}
interface Respuesta {
  filas: Fila[];
  resumen: { productos: number; clientesConExcepcion: number };
}
interface ResultadoMasivo {
  productos: number;
  sumaPorUnidad: string;
  omitidos: number;
  detalleOmitidos: Array<{ sku: string; nombre: string; motivo: string }>;
  ejemplos: Array<{ sku: string; monto: string }>;
  aplicado: boolean;
}
interface Lista {
  id: string;
  codigo: string;
  nombre: string;
  uso: string;
  activa: boolean;
  semantica: string;
  empresa_id: string;
}

const MAXIMO_FILAS = 400;

export function CostoLogistico() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const editable = puede('ventas.costo_logistico.gestionar');
  const listas = useQuery({
    queryKey: ['config', 'listas-venta', empresaId],
    queryFn: async () =>
      (await api.get<Lista[]>('/precios/listas')).filter(
        (l) => l.empresa_id === empresaId && l.uso === 'venta' && l.activa,
      ),
  });
  const [listaElegida, setLista] = useState('');
  const listaId = listaElegida || listas.data?.[0]?.id || '';
  const clave = ['config', 'costo-logistico', empresaId, listaId];
  const tarifa = useQuery({
    queryKey: clave,
    enabled: !!listaId,
    queryFn: () =>
      api.get<Respuesta>(
        `/ventas/costo-logistico?empresaId=${empresaId}&listaId=${encodeURIComponent(listaId)}`,
      ),
  });
  const [buscar, setBuscar] = useState('');
  const [soloCon, setSoloCon] = useState(false);
  const [edicion, setEdicion] = useState<Record<string, string>>({});
  const [porcentaje, setPorcentaje] = useState('');
  const [redondeo, setRedondeo] = useState(1);
  const [soloSinRecargo, setSoloSinRecargo] = useState(false);
  const [simulado, setSimulado] = useState<ResultadoMasivo | null>(null);

  const filtradas = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return (tarifa.data?.filas ?? []).filter(
      (f) =>
        (!t || coincide(t, f.sku, f.nombre)) &&
        (!soloCon || f.general),
    );
  }, [tarifa.data, buscar, soloCon]);
  const pendientes = Object.entries(edicion).filter(([id, v]) => {
    const f = tarifa.data?.filas.find((x) => x.productoId === id);
    return f && (v.trim() || null) !== (f.general ? String(Number(f.general)) : null);
  });

  const refrescar = () =>
    qc.invalidateQueries({ queryKey: ['config', 'costo-logistico', empresaId] });
  const guardar = useMutation({
    mutationFn: () =>
      api.put('/ventas/costo-logistico', {
        empresaId,
        cambios: pendientes.map(([productoId, v]) => ({
          productoId,
          monto: v.trim() ? String(Number(v.replace(/\./g, '').replace(',', '.'))) : null,
        })),
        motivo: 'Edición de la tarifa de costo logístico',
      }),
    onSuccess: async () => {
      setEdicion({});
      await refrescar();
    },
  });
  const masivo = useMutation({
    mutationFn: (aplicar: boolean) =>
      api.post<ResultadoMasivo>('/ventas/costo-logistico/masivo', {
        empresaId,
        listaId,
        porcentaje: Number(porcentaje.replace(',', '.')),
        redondeo,
        soloSinRecargo,
        // Con un filtro escrito, solo a los productos que se ven.
        ...(buscar.trim() || soloCon ? { productoIds: filtradas.map((f) => f.productoId) } : {}),
        aplicar,
      }),
    onSuccess: async (r) => {
      setSimulado(r);
      if (r.aplicado) await refrescar();
    },
  });
  const pct = Number(porcentaje.replace(',', '.'));
  const pctValido = Number.isFinite(pct) && pct > 0 && pct < 100;

  const columnas: Columna<Fila>[] = [
    {
      id: 'sku',
      titulo: 'Código',
      celda: (f) => <span className="mono">{f.sku}</span>,
      ancho: '8rem',
    },
    { id: 'nombre', titulo: 'Producto', celda: (f) => f.nombre },
    {
      id: 'precio',
      titulo: 'Precio lista (UN)',
      celda: (f) => (f.precioBase ? pesos(f.precioBase) : <span className="apagado">—</span>),
      alinear: 'der',
      ancho: '9rem',
    },
    {
      id: 'piso',
      titulo: 'Precio más bajo',
      celda: (f) => (f.piso ? pesos(f.piso) : <span className="apagado">—</span>),
      alinear: 'der',
      ancho: '9rem',
    },
    {
      id: 'recargo',
      titulo: 'Costo logístico con IVA (UN)',
      celda: (f) =>
        editable ? (
          <input
            className="mono entrada-corta"
            inputMode="decimal"
            aria-label={`Costo logístico ${f.sku}`}
            placeholder="—"
            value={edicion[f.productoId] ?? (f.general ? String(Number(f.general)) : '')}
            onChange={(e) => setEdicion({ ...edicion, [f.productoId]: e.target.value })}
          />
        ) : f.general ? (
          pesos(f.general)
        ) : (
          '—'
        ),
      alinear: 'der',
      ancho: '10rem',
    },
    {
      id: 'pct',
      titulo: '% de la lista',
      celda: (f) =>
        f.general && f.precioBase
          ? `${((Number(f.general) * 100) / Number(f.precioBase)).toFixed(1).replace('.', ',')} %`
          : '',
      alinear: 'der',
      ancho: '7rem',
    },
  ];

  return (
    <div className="pantalla costo-logistico" data-pantalla="costo-logistico">
      <header className="encabezado-entidad">
        <span className="ruta-migas">Configuración › Comercial</span>
        <h2>Costo logístico por producto</h2>
      </header>
      <p className="apagado">
        Como en Random: al facturar una orden de despacho con «Desglose logístico» marcado, en cada
        factura este monto <b>con IVA</b> por unidad sale del precio del producto y va a una línea{' '}
        <b>LOGISTICA</b> (con IVA, sin impuestos adicionales: el ILA o IABA del producto se calcula
        sin la logística). El cliente paga lo mismo. Las boletas no se tocan. Un recargo nunca llega
        al precio más bajo del producto.{' '}
        <b>Pendiente la validación del contador antes de usarlo en producción.</b>
      </p>
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Lista de precios</span>
          <select
            aria-label="Lista de precios"
            value={listaId}
            onChange={(e) => {
              setLista(e.target.value);
              setSimulado(null);
            }}
          >
            {(listas.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.codigo} · {l.nombre}
                {l.semantica !== 'bruto' ? ' (neta: sin cálculo)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar producto"
            placeholder="código o nombre"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </label>
        <label className="campo campo-check">
          <input type="checkbox" checked={soloCon} onChange={(e) => setSoloCon(e.target.checked)} />
          Solo con costo logístico
        </label>
        <span className="apagado">
          {tarifa.data?.resumen.productos ?? 0} productos con costo logístico
        </span>
      </div>

      {editable && (
        <fieldset className="panel-masivo" aria-label="Carga masiva">
          <legend>Carga masiva</legend>
          <label className="campo campo-corto">
            <span className="etiqueta">% del precio de la lista</span>
            <input
              aria-label="Porcentaje de la lista"
              inputMode="decimal"
              placeholder="ej. 50"
              value={porcentaje}
              onChange={(e) => {
                setPorcentaje(e.target.value);
                setSimulado(null);
              }}
            />
          </label>
          <label className="campo campo-corto">
            <span className="etiqueta">Redondear a</span>
            <select
              aria-label="Redondear a"
              value={redondeo}
              onChange={(e) => {
                setRedondeo(Number(e.target.value));
                setSimulado(null);
              }}
            >
              <option value={1}>$1</option>
              <option value={10}>$10</option>
              <option value={50}>$50</option>
              <option value={100}>$100</option>
            </select>
          </label>
          <label className="campo campo-check">
            <input
              type="checkbox"
              checked={soloSinRecargo}
              onChange={(e) => {
                setSoloSinRecargo(e.target.checked);
                setSimulado(null);
              }}
            />
            Solo a los que no tienen (no pisar)
          </label>
          <span className="apagado">
            Se aplica a{' '}
            {buscar.trim() || soloCon
              ? `los ${filtradas.length} productos filtrados`
              : 'todos los productos de la lista'}
            .
          </span>
          <button
            type="button"
            disabled={!pctValido || masivo.isPending}
            onClick={() => masivo.mutate(false)}
          >
            Simular
          </button>
          {simulado && !simulado.aplicado && (
            <button
              type="button"
              className="primario"
              disabled={masivo.isPending || simulado.productos === 0}
              onClick={() => masivo.mutate(true)}
            >
              Aplicar a {simulado.productos} productos
            </button>
          )}
          {simulado && (
            <div role="status" className="resultado-masivo">
              {simulado.aplicado ? 'Aplicado: ' : 'Simulación: '}
              <b>{simulado.productos}</b> productos
              {simulado.ejemplos.length > 0 &&
                ` (ej. ${simulado.ejemplos
                  .slice(0, 3)
                  .map((x) => `${x.sku} ${pesos(x.monto)}`)
                  .join(', ')})`}
              {simulado.omitidos > 0 && (
                <details>
                  <summary>{simulado.omitidos} se omiten</summary>
                  <ul>
                    {simulado.detalleOmitidos.map((o) => (
                      <li key={o.sku}>
                        <span className="mono">{o.sku}</span> {o.nombre}: {o.motivo}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {masivo.isError && <PanelError error={masivo.error} />}
        </fieldset>
      )}

      {tarifa.isError && <PanelError error={tarifa.error} />}
      <Grilla
        etiqueta="Costo logístico por producto"
        columnas={columnas}
        filas={filtradas.slice(0, MAXIMO_FILAS)}
        clave={(f) => f.productoId}
        cargando={tarifa.isLoading || listas.isLoading}
        vacio="No hay productos para esta lista"
      />
      {filtradas.length > MAXIMO_FILAS && (
        <p className="apagado">
          Se muestran {MAXIMO_FILAS} de {filtradas.length}: usa el buscador para llegar al resto.
        </p>
      )}
      {editable && (
        <div className="barra-accion-od">
          <span>{pendientes.length} cambio(s) sin guardar · vacío = quitar el costo logístico</span>
          {guardar.isError && <PanelError error={guardar.error} />}
          <button
            type="button"
            className="primario"
            disabled={!pendientes.length || guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
}
